import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { UseContentfulPageResponseModel } from '~/composables/useContentfulPage';
import { collapseContentCenterSlug } from '~/utils/contentful/collapseContentCenterSlug';
import { pagePayloadMatchesRoute } from '~/utils/contentful/pagePayloadMatchesRoute';
import { resolveContentfulPageRouteMode } from '~/utils/contentful/resolveContentfulPageRouteMode';

export type ContentfulPayloadForRoute =
  /** CMS route and the payload belongs to it. */
  | { status: 'matched'; payload: UseContentfulPageResponseModel }
  /** Shop, payment return, … — there is no CMS payload for this route. */
  | { status: 'not-cms-route' }
  /** CMS route, but the payload is missing or still belongs to another page. */
  | { status: 'mismatch' };

/**
 * Returns a resolver that waits for the Contentful fetch of a route and only
 * hands out the shared `$contentfulPagePayload` if it belongs to that route.
 *
 * Consumers running in `router.afterEach` or in watchers (Ninetailed page
 * event, GTM topics) must use this instead of reading the payload directly:
 * it can still hold the previous page, e.g. after an aborted or overlapping
 * fetch.
 *
 * Call it during plugin/component setup; the returned function is safe to call
 * after `await` (it does not depend on the Nuxt context).
 */
export function useContentfulPayloadForRoute(): (
  route: Pick<RouteLocationNormalizedLoaded, 'path' | 'params'>
) => Promise<ContentfulPayloadForRoute> {
  const nuxtApp = useNuxtApp();
  const { $contentfulPagePayload, $globalPageSettings } = nuxtApp;

  const collapseSlug = (slug: string): string =>
    collapseContentCenterSlug(
      slug,
      $globalPageSettings?.value?.documentCenterPage?.slug ?? undefined,
      $globalPageSettings?.value?.softwareCenterPage?.slug ?? undefined
    );

  return async (route) => {
    if (resolveContentfulPageRouteMode(route) === false) {
      return { status: 'not-cms-route' };
    }

    const matchingPayload = (): UseContentfulPageResponseModel | null => {
      const payload = $contentfulPagePayload.value;
      return pagePayloadMatchesRoute(payload, route, collapseSlug)
        ? payload
        : null;
    };

    try {
      // `afterEach` fires before the payload of the new route is resolved.
      // Retry once: a concurrent navigation may have superseded the first sync.
      for (let attempt = 0; attempt < 2; attempt++) {
        await nuxtApp._ensureContentfulPageSyncFetch?.();

        const payload = matchingPayload();
        if (payload) {
          return { status: 'matched', payload };
        }
      }
    } catch (e) {
      useLogger().error('Contentful payload sync error', e);
    }

    return { status: 'mismatch' };
  };
}
